import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { PiggyBank, Loader2 } from 'lucide-react';
import { AdminPartnerSettings } from './AdminPartnerSettings';
import { AdminWhiSettings } from './AdminWhiSettings';

const API = process.env.REACT_APP_BACKEND_URL;
const tzs = (n) => `TZS ${Math.round(n || 0).toLocaleString('en-US')}`;
const STAGE_LABELS = { checklist: 'Preparing', fund: 'Choosing fund', register: 'Registering', account: 'Opening acct', fund_account: 'Ready to fund', funded: 'Funded', allocated: 'Allocated' };
const STATUS_CLS = { pending_payment: 'bg-amber-50 text-amber-700', paid: 'bg-sky-50 text-sky-700', allocated: 'bg-emerald-50 text-emerald-700', failed: 'bg-red-50 text-red-700' };

const AllocateModal = ({ order, adminKey, onClose, onDone }) => {
  const [form, setForm] = useState({ units: order.est_units, nav: order.nav_at_order, utt_reference: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/utt/orders/${order.order_id}/allocate`,
        { units: Number(form.units), nav: Number(form.nav), utt_reference: form.utt_reference }, { headers: { 'X-Admin-Key': adminKey } });
      onDone();
    } catch (e2) {
      setErr(e2.response?.data?.detail?.toString() || 'Failed');
      setBusy(false);
    }
  };
  const field = (key, label, type = 'text') => (
    <label className="block text-xs text-slate-600 mb-3">{label}
      <input type={type} step="any" required value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid={`admin-utt-allocate-${key}`} />
    </label>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4" onClick={() => !busy && onClose()} data-testid="admin-utt-allocate-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white p-6">
        <h3 className="mb-1 text-lg font-bold text-slate-900">Allocate UTT units</h3>
        <p className="mb-4 text-xs text-slate-500">{order.client_name} · {order.fund_name} · {tzs(order.amount_tzs)} · UTT acct <code>{order.utt_account_number}</code></p>
        {field('units', 'Units allocated by UTT AMIS', 'number')}
        {field('nav', 'NAV / sale price used', 'number')}
        {field('utt_reference', 'UTT AMIS transaction / contract reference')}
        {err && <p className="mb-2 text-xs text-red-600">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button type="submit" disabled={busy} className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white" data-testid="admin-utt-allocate-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm allocation'}
          </button>
        </div>
      </form>
    </div>
  );
};

const NavSyncPanel = ({ adminKey }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const h = { headers: { 'X-Admin-Key': adminKey } };
  const load = useCallback(() => axios.get(`${API}/api/admin/utt/nav-status`, { headers: { 'X-Admin-Key': adminKey } }).then((r) => setSt(r.data)).catch(() => {}), [adminKey]);
  useEffect(() => { if (adminKey) load(); }, [adminKey, load]);
  const syncNow = async () => {
    setBusy(true);
    await axios.post(`${API}/api/admin/utt/nav-sync`, {}, h).catch(() => {});
    await load();
    setBusy(false);
  };
  if (!st) return null;
  const last = st.last_run;
  return (
    <div className="mb-5 rounded-xl border border-slate-200 p-4" data-testid="admin-utt-nav-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-sm">
          <span className="font-semibold text-slate-900">Daily NAV sync</span>
          <span className="ml-2 text-xs text-slate-500" data-testid="admin-utt-nav-last">
            Last run {last ? new Date(last.started_at).toLocaleString() : '—'} ({last?.trigger || '—'}) ·{' '}
            {last?.ok ? <span className="text-emerald-700">OK</span> : <span className="text-red-600" title={last?.error}>FAILED</span>}
            {' '}· prices as of <b>{st.funds[0]?.nav_as_of}</b> · auto Mon–Sat 10:00 EAT
          </span>
        </div>
        <button onClick={syncNow} disabled={busy} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-900 text-white disabled:opacity-60" data-testid="admin-utt-nav-sync-btn">
          {busy ? 'Syncing…' : 'Sync now'}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
        {st.funds.map((f) => (
          <div key={f.id} className={`rounded-lg p-2 text-xs ${f.stale ? 'bg-amber-50' : 'bg-slate-50'}`} data-testid={`admin-utt-nav-${f.id}`}>
            <div className="font-medium text-slate-700 truncate">{f.name}</div>
            <div className="text-slate-900 font-bold">{Number(f.nav).toLocaleString('en-US', { maximumFractionDigits: 4 })}</div>
            <div className="text-slate-500">12m {f.annual_return}% · YTD {f.ytd_return}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminUTTOrders = ({ adminKey }) => {
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [allocating, setAllocating] = useState(null);

  const load = useCallback(async () => {
    const h = { headers: { 'X-Admin-Key': adminKey } };
    const [o, f] = await Promise.all([axios.get(`${API}/api/admin/utt/orders`, h), axios.get(`${API}/api/admin/utt/funnel`, h)]);
    setData(o.data);
    setFunnel(f.data);
  }, [adminKey]);

  useEffect(() => { if (adminKey) load().catch(() => {}); }, [adminKey, load]);

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="admin-utt-tile">
      <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2"><PiggyBank className="w-4 h-4 text-emerald-700" /> UTT AMIS onboarding & orders</h2>
      <p className="text-xs text-slate-500 mb-4">Clients funded via Selcom. Forward paid orders to UTT AMIS through CORE Securities, then record the allocated units — the client portfolio updates automatically.</p>
      <AdminPartnerSettings adminKey={adminKey} />
      <AdminWhiSettings adminKey={adminKey} />
      <NavSyncPanel adminKey={adminKey} />
      {funnel && (
        <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 mb-4" data-testid="admin-utt-funnel">
          {Object.entries(funnel.stages).map(([k, v]) => (
            <div key={k} className="rounded-xl bg-slate-50 p-2 text-center">
              <div className="text-lg font-bold text-slate-900" data-testid={`admin-utt-funnel-${k}`}>{v}</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">{STAGE_LABELS[k] || k}</div>
            </div>
          ))}
        </div>
      )}
      {data?.rows?.length ? (
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-xs uppercase tracking-wide">
              <tr className="border-b border-slate-200"><th className="text-left py-2">Client</th><th className="text-left py-2">Fund / UTT acct</th><th className="text-right py-2">Amount</th><th className="text-center py-2">Status</th><th className="text-right py-2">Action</th></tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.order_id} className="border-b border-slate-100" data-testid={`admin-utt-row-${r.order_id}`}>
                  <td className="py-2"><div className="font-medium text-slate-900">{r.client_name || '—'}</div><div className="text-xs text-slate-500">{r.phone} · {r.client_email}</div></td>
                  <td className="py-2 text-xs"><div>{r.fund_name}</div><code className="text-slate-600">{r.utt_account_number}</code></td>
                  <td className="py-2 text-right text-emerald-700 whitespace-nowrap">{tzs(r.amount_tzs)}</td>
                  <td className="py-2 text-center"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLS[r.status] || ''}`}>{r.status.replace('_', ' ')}{r.simulated ? ' (demo)' : ''}</span></td>
                  <td className="py-2 text-right">
                    {r.status === 'paid' ? (
                      <button onClick={() => setAllocating(r)} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-700 text-white hover:bg-emerald-800" data-testid={`admin-utt-allocate-${r.order_id}`}>Allocate</button>
                    ) : r.status === 'allocated' ? <span className="text-xs text-slate-500">{r.units_allocated} u · {r.utt_reference}</span> : <span className="text-xs text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm text-slate-500">No UTT orders yet.</p>}
      {allocating && <AllocateModal order={allocating} adminKey={adminKey} onClose={() => setAllocating(null)} onDone={() => { setAllocating(null); load(); }} />}
    </div>
  );
};
